import React, { useEffect, useRef, useState } from "react";
import Select, { components, type MenuListProps } from "react-select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ButtonLoadingContent, loadingButtonClass } from "@/components/buttons/ButtonLoadingContent";
import { Button } from "@/components/ui/button";
import { LABELS } from "@/utils/constants/appConstants/commonConstants";
import { fetchUsers } from "@/services/user.service";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";

interface User {
    id: string;
    first_name: string;
    last_name: string;
    email_address: string;
}

interface UserOption {
    label: string;
    value: string;
    user: User;
}

interface TransferOwnershipModalProps {
    show?: boolean;
    onHide?: () => void;
    onTransferOwnership: (selectedUser: UserOption) => void;
    loading?: boolean;
    title?: string;
    description?: string;
    currentOwnerId?: string;
}

const TransferOwnershipMenuList = (props: MenuListProps<UserOption, false>) => (
    <components.MenuList
        {...props}
        innerProps={{
            ...props.innerProps,
            onWheel: (event) => {
                props.innerProps?.onWheel?.(event);
                event.stopPropagation();
            },
        }}
    />
);

const TransferOwnershipModal: React.FC<TransferOwnershipModalProps> = ({
    show,
    onHide,
    onTransferOwnership,
    loading = false,
    title = LABELS.TRANSFER_OWNERSHIP,
    description,
    currentOwnerId,
}) => {
    const [users, setUsers] = useState<UserOption[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<UserOption[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [isFetchingUsers, setIsFetchingUsers] = useState(false);
    const lastFetchKeyRef = useRef("");

    const getFilteredUsers = (allUsers: UserOption[], term: string) => {
        const normalizedTerm = term.trim().toLowerCase();
        return allUsers.filter((userOption) => {
            if (userOption.user.id === currentOwnerId) {
                return false;
            }
            if (normalizedTerm.length < 2) {
                return true;
            }
            return (
                userOption.label.toLowerCase().includes(normalizedTerm) ||
                userOption.user.email_address.toLowerCase().includes(normalizedTerm)
            );
        });
    };

    useEffect(() => {
        const fetchKey = `${show ? "open" : "closed"}:${currentOwnerId ?? ""}`;
        if (show && lastFetchKeyRef.current !== fetchKey) {
            lastFetchKeyRef.current = fetchKey;
            fetchUsersForTransfer();
        }
    }, [show, currentOwnerId]);

    useEffect(() => {
        if (!show) {
            setSelectedUser(null);
            setSearchTerm("");
            setFilteredUsers(getFilteredUsers(users, ""));
        }
    }, [show, users, currentOwnerId]);

    useEffect(() => {
        if (!show) {
            return;
        }
        setFilteredUsers(getFilteredUsers(users, searchTerm));
        if (selectedUser && currentOwnerId && selectedUser.user.id === currentOwnerId) {
            setSelectedUser(null);
        }
    }, [currentOwnerId, searchTerm, show, users, selectedUser]);

    const fetchUsersForTransfer = async () => {
        setIsFetchingUsers(true);
        try {
            await fetchUsers(
                1,
                100,
                {},
                {},
                null,
                (response: any) => {
                    const currentOrg = getCurrentOrganization();
                    const orgFilteredUsers = response.data.users.filter((user: any) => {
                        if (currentOrg && user.organizations) {
                            return user.organizations.some(
                                (org: any) => org.organization === currentOrg.organization
                            );
                        }
                        return true;
                    });
                    const userOptions = orgFilteredUsers.map((user: User) => ({
                        label: `${user.first_name} ${user.last_name} (${user.email_address})`.trim(),
                        value: user.id,
                        user,
                    }));
                    setUsers(userOptions);
                    setFilteredUsers(getFilteredUsers(userOptions, searchTerm));
                    setIsFetchingUsers(false);
                },
                (error: any) => {
                    console.error("Error fetching users:", error);
                    setUsers([]);
                    setFilteredUsers([]);
                    setIsFetchingUsers(false);
                }
            );
        } catch (error) {
            console.error("Error fetching users:", error);
            setIsFetchingUsers(false);
        }
    };

    const handleUserSearch = (inputValue: string) => {
        setSearchTerm(inputValue);
        setFilteredUsers(getFilteredUsers(users, inputValue));
    };

    const handleClose = () => {
        setSelectedUser(null);
        setSearchTerm("");
        onHide?.();
    };

    return (
        <Dialog open={show} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <DialogContent className="gs-transfer-ownership-dialog">
                <div className="gs-transfer-ownership-dialog__header">
                    <h4 className="gs-transfer-ownership-dialog__title">{title}</h4>
                </div>

                <div className="gs-transfer-ownership-dialog__body">
                    {description ? (
                        <p className="gs-transfer-ownership-dialog__description">{description}</p>
                    ) : null}

                    <label className="gs-transfer-ownership-dialog__label" htmlFor="transfer-ownership-user-select">
                        {LABELS.SELECT_USER}
                    </label>

                    <Select<UserOption, false>
                        inputId="transfer-ownership-user-select"
                        className="transfer-ownership-select gs-transfer-ownership-dialog__select"
                        classNamePrefix="react-select"
                        placeholder={isFetchingUsers ? LABELS.LOADING_USERS : LABELS.SEARCH_FOR_USER}
                        value={selectedUser}
                        onChange={(selectedOption) => setSelectedUser(selectedOption)}
                        onInputChange={handleUserSearch}
                        options={filteredUsers}
                        isClearable
                        isSearchable
                        isDisabled={isFetchingUsers || loading}
                        filterOption={() => true}
                        maxMenuHeight={200}
                        closeMenuOnScroll={false}
                        menuShouldScrollIntoView={false}
                        components={{ MenuList: TransferOwnershipMenuList }}
                        noOptionsMessage={() => {
                            if (isFetchingUsers) {
                                return LABELS.LOADING_USERS;
                            }
                            return searchTerm.length < 2 ? LABELS.TYPE_TO_SEARCH : LABELS.NO_USERS_FOUND;
                        }}
                        formatOptionLabel={(option: UserOption) => (
                            <div>
                                <div>{option.label}</div>
                                <small className="text-muted">{option.user.email_address}</small>
                            </div>
                        )}
                    />
                </div>

                <div className="gs-transfer-ownership-dialog__footer">
                    <Button
                        id="transfer-ownership-btn"
                        className={loadingButtonClass(loading, "gs-transfer-ownership-dialog__submit")}
                        onClick={() => selectedUser && onTransferOwnership(selectedUser)}
                        disabled={!selectedUser || loading || isFetchingUsers}
                        aria-busy={loading || undefined}
                    >
                        <ButtonLoadingContent loading={loading} loaderId="transfer-ownership-btn-loader">
                            {LABELS.TRANSFER_OWNERSHIP}
                        </ButtonLoadingContent>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TransferOwnershipModal;
